import {
  SortOrder,
  type PaginationConfig,
  type RecordEntity,
  type RecordEntityId,
} from '../records';
import type {
  Category,
  CategoryToItemRelationship,
  Item,
  ItemToModifierGroupRelationship,
  LocalMenuGroup,
  Menu,
  Modifier,
  ModifierGroup,
  ModifierGroupToModifierRelationship,
  PriceLevelsMap,
  Visibility,
} from '../menu';

type XOR<A, B> = (A & { [K in keyof B]?: never }) | (B & { [K in keyof A]?: never });

export type MenuLabelsMap = Record<string, MenuLabel>;

export type MenuLabel = {
  id: string;
  text: string;
} & XOR<{ s3_link: string }, { image_data: string }>;

export interface GlobalMenuGroup extends RecordEntity {
  name: string;
  is_active: boolean;
  posid_segment: string;
  published_brands?: Brand[];
  draft_brands?: Brand[];
  version?: number;
  auto_push_to_local?: boolean;
  price_levels?: PriceLevelsMap;
  allowed_local_menu_groups?: LocalMenuGroup[];
  show_v4: boolean;
  menu_labels?: MenuLabelsMap;
  menuworks_unit_ids?: string[];
  visibility?: Visibility;
}
export interface Brand extends RecordEntity {
  id: RecordEntityId;
  name: string;
  is_active?: boolean;
  global_menu_group_id?: RecordEntityId;
  local_menu_group_id?: RecordEntityId;
  type: MenuRecordType;
  description?: string;
  attachments?: Attachments;
  global_menu_group?: GlobalMenuGroup;
  pending_attachment?: boolean;
  statuses?: BrandStatus[];
  parent_id?: RecordEntityId;
  version?: number;
  is_simplified_view?: boolean; // Deprecated
  frictionless_partner?: FrictionlessPartner;
  is_jwo: boolean;
  menus?: Menu[];
  catalog_id?: number;
  published_catalog_id?: number;
  meta?: BrandMeta;
  auto_push_to_local?: boolean;
  price_levels?: PriceLevelsMap;
  local_menu_group?: LocalMenuGroup;
  parent?: Brand;
  archived_at?: Date;
  menu_labels?: MenuLabelsMap;
  visibility?: Visibility;
}

export interface BrandMeta {
  jde_category: string;
}

export enum FrictionlessPartner {
  AWS = 'AWS',
}

export enum MenuRecordType {
  GLOBAL = 'global',
  LOCAL = 'local',
}

export interface Attachments {
  thumbnail?: any;
}

export interface BrandStatusMetaData {
  s3_link?: string;
  message?: string;
  process_id?: RecordEntityId;
  progress?: BrandStatusMetaDataProgress;
}

export interface BrandStatus {
  brand_id: RecordEntityId;
  id: RecordEntityId;
  status: BrandStatusSeverity;
  type: BrandStatusType;
  metadata?: BrandStatusMetaData;
  created_at?: string | number | Date;
  updated_at?: string | number | Date;
  version?: number;
}

export type IntegrationPartnerProgress = Record<
  string,
  Record<RecordEntityId, Record<RecordEntityId, BrandStatusSeverity>>
>;

export interface MenuWorksSyncProgress {
  total: number;
  processed: number;
  failed: number;
  skipped: number;
}

export type BrandStatusMetaDataProgress =
  | IntegrationPartnerProgress
  | Partial<Record<BrandStatusType, MenuWorksSyncProgress>>;

export enum BrandStatusSeverity {
  OK = 'successful',
  INITIATED = 'initiated',
  IN_PROGRESS = 'in_progress',
  ERROR = 'failed',
}

export enum BrandStatusType {
  PUBLISH = 'publish',
  POSID = 'posid_generation',
  APPLY_DIFF = 'apply_diff',
  IMPORT_BRAND_EXCEL = 'import_brand_excel',
  UPDATE_INTEGRATED_PARTNERS = 'update_integrated_partners',
  MENUWORKS_SYNC = 'menuworks_sync',
}

export enum DiffAction {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
}

export enum DiffEntityType {
  MODIFIER = 'modifier',
  MODIFIER_GROUP = 'modifier_group',
  ITEM = 'item',
  CATEGORY = 'category',
  VIEW = 'layout',
}

export interface BrandDiffRecord extends RecordEntity {
  action: DiffAction;
  entity_type: DiffEntityType;
  local_id: RecordEntityId;
  changes: Modifier | ModifierGroup | Item | Category | Menu;
  local_snapshot:
    | Modifier
    | ModifierGroup
    | Item
    | Category
    | Menu
    | CategoryToItemRelationship
    | ItemToModifierGroupRelationship
    | ModifierGroupToModifierRelationship;
  global_id: RecordEntityId;
  global_snapshot:
    | Modifier
    | ModifierGroup
    | Item
    | Category
    | Menu
    | CategoryToItemRelationship
    | ItemToModifierGroupRelationship
    | ModifierGroupToModifierRelationship;
  brand_id: RecordEntityId;
  depends_on: BrandDiffRecord[];
  child_nodes: BrandDiffRecord[];
  name: string;
}

export interface BrandDiff {
  id: string;
  overwrite: boolean;
}

export interface BrandDiffPayload {
  diffs: BrandDiff[];
}

export const defaultBrandNavigationPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'name',
    sortOrder: SortOrder.ASC,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

export interface BrandPublishError {
  barcode: string;
  items: WarningItemDTO[];
  type: string;
  brand_id: RecordEntityId;
  brand_name: string;
}
export interface WarningItemDTO {
  id: string;
  item_name: string;
  posid: string;
}
